import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { memberGenderListboxOptions, type MemberGender } from '@/lib/gender';

export type GroupGender = MemberGender;

export interface GroupAudienceValue {
  gender: GroupGender | null;
  minAge: string;
  maxAge: string;
}

interface GroupAudienceFieldsProps {
  value: GroupAudienceValue;
  onChange: (next: GroupAudienceValue) => void;
  popoverPortal?: PopoverPortalTarget;
  showGender?: boolean;
  showAge?: boolean;
}

export const emptyAudience = (): GroupAudienceValue => ({
  gender: null,
  minAge: '',
  maxAge: '',
});

export const audiencePayload = (value: GroupAudienceValue) => {
  const minAge = value.minAge.trim() === '' ? null : Number(value.minAge);
  const maxAge = value.maxAge.trim() === '' ? null : Number(value.maxAge);
  return {
    gender: value.gender,
    minAge: Number.isFinite(minAge) ? minAge : null,
    maxAge: Number.isFinite(maxAge) ? maxAge : null,
  };
};

export const audienceFromRecord = (input: {
  gender?: GroupGender | null;
  minAge?: number | null;
  maxAge?: number | null;
}): GroupAudienceValue => ({
  gender: input.gender ?? null,
  minAge: input.minAge == null ? '' : String(input.minAge),
  maxAge: input.maxAge == null ? '' : String(input.maxAge),
});

export const audienceFromTemplateParams = (
  params: Record<string, unknown> | null | undefined,
): GroupAudienceValue =>
  audienceFromRecord({
    gender: typeof params?.gender === 'string' ? (params.gender as GroupGender) : null,
    minAge: typeof params?.minAge === 'number' ? params.minAge : null,
    maxAge: typeof params?.maxAge === 'number' ? params.maxAge : null,
  });

export const demographicTemplateParams = (
  templateId: string,
  value: GroupAudienceValue,
): Record<string, unknown> => {
  const audience = audiencePayload(value);
  const params: Record<string, unknown> = {};
  if (templateId === 'gender' || templateId === 'demographics') {
    if (audience.gender) params.gender = audience.gender;
  }
  if (templateId === 'age_band' || templateId === 'demographics') {
    if (audience.minAge != null) params.minAge = audience.minAge;
    if (audience.maxAge != null) params.maxAge = audience.maxAge;
  }
  return params;
};

export const GroupAudienceFields = ({
  value,
  onChange,
  popoverPortal = 'dialog',
  showGender = true,
  showAge = true,
}: GroupAudienceFieldsProps) => {
  const { t } = useTranslation('groups');
  const { t: tPeople } = useTranslation('people');

  return (
    <div className="flex flex-col gap-3">
      <p className="text-body-sm text-fg-secondary">{t('audience.hint')}</p>
      {showGender ? (
        <div className="flex flex-col gap-1.5">
          <Label>{t('audience.gender')}</Label>
          <Listbox
            value={value.gender}
            onChange={(next) => onChange({ ...value, gender: next as GroupGender | null })}
            options={memberGenderListboxOptions(tPeople)}
            placeholder={t('audience.genderAny')}
            clearable
            clearLabel={t('audience.genderAny')}
            popoverPortal={popoverPortal}
          />
        </div>
      ) : null}
      {showAge ? (
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="group-min-age">{t('audience.minAge')}</Label>
            <Input
              id="group-min-age"
              type="number"
              inputMode="numeric"
              min={0}
              max={150}
              value={value.minAge}
              onChange={(event) => onChange({ ...value, minAge: event.target.value })}
              placeholder={t('audience.ageAny')}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="group-max-age">{t('audience.maxAge')}</Label>
            <Input
              id="group-max-age"
              type="number"
              inputMode="numeric"
              min={0}
              max={150}
              value={value.maxAge}
              onChange={(event) => onChange({ ...value, maxAge: event.target.value })}
              placeholder={t('audience.ageAny')}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};
